import axios from "../../../axios";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as dataActions from "../../data/orders";
import { AsyncAppThunkAction } from "../../../redux-store";
import { isAxiosError } from "axios";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { orderT } from "./types";
import { loadStopUnitsInfoFromSharedCargo } from "../cargo-dialog";
import { StopUnitTemplate } from "../../../ducks/data/stop-unit-templates";
import { notifyL } from "../../notifications";
import { orderStatusT } from "dora-contracts";

export const loadOrder =
  (id: string): AsyncAppThunkAction =>
  async (dispatch) => {
    dispatch(slice.actions.setStatus("OK"));
    dispatch(slice.actions.clearSharedStopUnitsInfo());
    try {
      await dispatch(dataActions.loadOrder(id)).unwrap();
    } catch {
      // The loadOrder thunk has already reported it and shown the error toast;
      // OrderModal dispatches this fire-and-forget.
    }
  };

const prefix = "app/order-dialog";

type State = {
  status: "OK" | "ACCEPT_CONFLICT" | "REJECT_CONFLICT";
  sharedStopUnitsInfo: StopUnitTemplate[] | null;
};

const initialState: State = {
  status: "OK",
  sharedStopUnitsInfo: null,
};

const conflictT = t.strict({ current: orderT, status: orderStatusT });

// A 409 on accept/reject is either a stale revision on a still-open order
// (show the conflict alert so the dispatcher can review the changes), or the
// order was already accepted/rejected elsewhere, e.g. by another dispatcher or
// in a tab that missed the update. In the latter case there is nothing left
// to do: refresh, tell the user, and let the dialog close.
const handleConflict =
  (action: "ACCEPT" | "REJECT", data: unknown): AsyncAppThunkAction<boolean> =>
  async (dispatch) => {
    const { current, status } = await tPromise.decode(conflictT, data);
    dispatch(dataActions.setOrder(current));
    if (status === "CREATED") {
      dispatch(slice.actions.setStatus(`${action}_CONFLICT`));
      return false;
    }
    dispatch(
      notifyL({
        type: "info",
        namespace: "modals",
        key:
          status === "REJECTED"
            ? "orderModal.alreadyRejected"
            : "orderModal.alreadyAccepted",
      })
    );
    await dispatch(dataActions.loadOrders());
    return true;
  };

export const rejectOrder =
  (id: string): AsyncAppThunkAction<boolean> =>
  async (dispatch, getState) => {
    const order = getState().data.orders.entities[id];
    try {
      await axios.post(`/api/orders/${id}/reject`, {
        revision: order.revision,
      });
      await dispatch(dataActions.loadOrders());
      return true;
    } catch (e) {
      if (!isAxiosError(e)) {
        throw e;
      }
      if (e.response?.status === 409 && e.response?.data) {
        return dispatch(handleConflict("REJECT", e.response.data));
      }
      return false;
    }
  };

export const acceptOrder =
  (
    id: string,
    stopUnitTemplateIdMapping?: Record<string, string>
  ): AsyncAppThunkAction<boolean> =>
  async (dispatch, getState) => {
    const order = getState().data.orders.entities[id];
    try {
      await axios.post(`/api/orders/${id}/accept`, {
        revision: order.revision,
        ...(stopUnitTemplateIdMapping && { stopUnitTemplateIdMapping }),
      });
      await dispatch(dataActions.loadOrders());
      return true;
    } catch (e) {
      if (!isAxiosError(e)) {
        throw e;
      }
      if (e.response?.status === 409 && e.response?.data) {
        return dispatch(handleConflict("ACCEPT", e.response.data));
      }
      return false;
    }
  };

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<State["status"]>) => {
      state.status = action.payload;
    },
    clearSharedStopUnitsInfo: (state) => {
      state.sharedStopUnitsInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      loadStopUnitsInfoFromSharedCargo.fulfilled,
      (state, action) => {
        state.sharedStopUnitsInfo = action.payload;
      }
    );
  },
});

export const { setStatus } = slice.actions;
export default slice.reducer;
